//Common
import { PAGINACAO } from "@/common/utils/paginacao";
import {
  fn_loadOpcoesTipoApuracao,
  fn_loadOpcoesTipoLancamento,
  fn_loadOpcoesTipoPeriodicidadeReajuste,
} from "@/common/siig";
import {
  fn_dataIsAfter,
  fn_dataIsBefore,
  fn_getDataAtual,
  fn_getDifDiasDatas,
  fn_getDifMesesDatas,
  fn_getMesAnoAtual,
  fn_loadOpcoesMeses,
  fn_validarData,
  fn_validarMesAno,
} from "@/common/utils/date";

//Util
import { MENSAGENS_ERRO, showMensagemErro } from "./mensagens";

//API
import { api_loadOpcoes, ROTA_LOAD } from "../services/api/api_load";

//Retorna página que o item se encontra verificando pelo id
export function fn_getPaginaItem(pagamentos, qtdeRegistrosPagina, id) {
  const index = pagamentos.findIndex((pag) => pag.id == id);

  if (index < 0) {
    return 1;
  }

  return Math.floor(index / qtdeRegistrosPagina) + 1;
}

export function fn_getPaginaNumPag(
  numPag,
  registrosPorPagina = PAGINACAO.ITENS_POR_PAGINA_DEFAULT,
) {
  return Math.ceil(numPag / registrosPorPagina);
}

/*
 * Verifica se a senha é válida de acordo com as regras definidas
 * 1 - Mínimo 6 caracteres
 * 2 - Ter pelo menos uma letra
 * 3 - Ter pelo menos um número
 */
export function fn_validarSenha(senha) {
  if (!senha || senha.length < 6) {
    showMensagemErro(MENSAGENS_ERRO.SENHA_QTDE_MIN_CARACTERES, 6);
    return false;
  }

  if (!/[A-Za-z]/.test(senha)) {
    showMensagemErro(MENSAGENS_ERRO.SENHA_PELO_MENOS_UMA_LETRA);
    return false;
  }

  if (!/\d/.test(senha)) {
    showMensagemErro(MENSAGENS_ERRO.SENHA_PELO_MENOS_UM_NUMERO);
    return false;
  }

  return true; // senha válida
}

// Valida o período de data, podendo informar o máximo de dias do período
export function fn_validarPeriodoData(
  formRef,
  nomeCampoDataInicio,
  nomeCampoDataFim,
  permiteDataFimPosteriorAtual = true,
  maxDias,
  complementoMsgMaxDias = "",
) {
  let errors = {};
  let valido = true;

  const body = formRef.current.getData();
  const dataInicio = body[nomeCampoDataInicio];
  const dataFim = body[nomeCampoDataFim];

  //Valida data início
  if (!fn_validarData(dataInicio)) {
    errors[nomeCampoDataInicio] = "inválido";
    valido = false;
  }

  //Valida data fim
  if (!fn_validarData(dataFim)) {
    errors[nomeCampoDataFim] = "inválido";
    valido = false;
  }

  if (valido) {
    //Se data fim for anterior à data início
    if (fn_dataIsBefore(dataFim, dataInicio)) {
      showMensagemErro(MENSAGENS_ERRO.DATA_FIM_POSTERIOR_OU_IGUAL_DATA_INICIO);
      errors[nomeCampoDataFim] = "inválido";
      valido = false;
    }
    //Se não permitir data fim posterior à data atual e for posterior
    else if (
      !permiteDataFimPosteriorAtual &&
      fn_dataIsAfter(dataFim, fn_getDataAtual())
    ) {
      showMensagemErro(MENSAGENS_ERRO.DATA_FIM_ANTERIOR_OU_IGUAL_DATA_ATUAL);
      errors[nomeCampoDataFim] = "inválido";
      valido = false;
    }
    //Se informou o máximo de dias do período
    else if (maxDias) {
      //Verifica se ultrapassou o máximo permitido
      const difDias = fn_getDifDiasDatas(dataInicio, dataFim);

      if (difDias + 1 > maxDias) {
        showMensagemErro(
          MENSAGENS_ERRO.PERIODO_MAX_DIAS,
          maxDias,
          complementoMsgMaxDias,
        );
        errors[nomeCampoDataFim] = "inválido";
        valido = false;
      }
    }
  } else {
    showMensagemErro(MENSAGENS_ERRO.DATA_INVALIDA);
  }

  formRef.current.setErrors(errors);

  return valido;
}

// Valida o período de mês/ano, podendo informar o máximo de meses do período
export function fn_validarPeriodoMesAno(
  formRef,
  nomeCampoMesAnoInicio,
  nomeCampoMesAnoFim,
  permiteMesAnoFimPosteriorAtual = true,
  maxMeses,
) {
  let errors = {};
  let valido = true;

  const body = formRef.current.getData();
  const mesAnoInicio = body[nomeCampoMesAnoInicio];
  const mesAnoFim = body[nomeCampoMesAnoFim];

  //Valida mês/ano início
  if (!fn_validarMesAno(mesAnoInicio)) {
    errors[nomeCampoMesAnoInicio] = "inválido";
    valido = false;
  }

  //Valida mês/ano fim
  if (!fn_validarMesAno(mesAnoFim)) {
    errors[nomeCampoMesAnoFim] = "inválido";
    valido = false;
  }

  if (valido) {
    //Se mês/ano fim for anterior à data início
    if (fn_dataIsBefore(mesAnoFim, mesAnoInicio)) {
      showMensagemErro(
        MENSAGENS_ERRO.MESANO_X_POSTERIOR_OU_IGUAL_MESANO_Y,
        "fim",
        "início",
      );
      errors[nomeCampoMesAnoFim] = "inválido";
      valido = false;
    }
    //Se não permitir mês/ano fim posterior ao mês/ano atual e for posterior
    else if (
      !permiteMesAnoFimPosteriorAtual &&
      fn_dataIsAfter(mesAnoFim, fn_getMesAnoAtual())
    ) {
      showMensagemErro(
        MENSAGENS_ERRO.MESANO_X_ANTERIOR_OU_IGUAL_MESANO_Y,
        "fim",
        "atual",
      );
      errors[nomeCampoMesAnoFim] = "inválido";
      valido = false;
    }
    //Se informou o máximo de meses do período
    else if (maxMeses) {
      //Verifica se ultrapassou o máximo permitido
      const difMeses = fn_getDifMesesDatas(mesAnoInicio, mesAnoFim);

      if (difMeses + 1 > maxMeses) {
        showMensagemErro(MENSAGENS_ERRO.PERIODO_MAX_MESES, maxMeses);
        errors[nomeCampoMesAnoFim] = "inválido";
        valido = false;
      }
    }
  } else {
    showMensagemErro(MENSAGENS_ERRO.MESANO_INVALIDO);
  }

  formRef.current.setErrors(errors);

  return valido;
}

//Carrega as opções de índice reajuste
async function fn_loadOpcoesIndiceReajuste() {
  const opcoes = await api_loadOpcoes(ROTA_LOAD.INDICE_REAJUSTE, `situacao=A`, {
    labelSigla: true,
  });
  opcoes.unshift({ value: "0", label: "Não reajustável" });

  return opcoes;
}

//Converte o valor do campo "opcoes" em opções para colocar no select
export async function fn_parseOptions(valorOpcoes) {
  if (!valorOpcoes) return [];

  //Funções que podem ser colocadas no campo opcoes da tabela CAMPO_CONTRATO
  const FUNCOES_SELECT_CONTRATO = {
    fn_loadOpcoesMeses,
    fn_loadOpcoesTipoPeriodicidadeReajuste,
    fn_loadOpcoesTipoApuracao,
    fn_loadOpcoesTipoLancamento,
    fn_loadOpcoesIndiceReajuste,
  };

  //Verifica se é uma FUNÇÃO
  if (valorOpcoes.startsWith("fn_")) {
    const fn = FUNCOES_SELECT_CONTRATO[valorOpcoes];

    if (!fn) {
      console.warn("Função de opções não encontrada:", valorOpcoes);
      return [];
    }

    const opcoes = await fn();
    return opcoes;
  }
  // Verifica se é uma ROTA para buscar as opções do banco
  else if (valorOpcoes.startsWith("/")) {
    const opcoes = await api_loadOpcoes(valorOpcoes, null, {
      labelSigla: true,
    });

    return opcoes;
  }
  //Verifica se o valor está cadastrado no formato valor1:label1,valor2:label2 (Ex: M:Matrícula,E:E-mail)
  else if (valorOpcoes.includes(":")) {
    return valorOpcoes.split(",").map((item) => {
      const [value, label] = item.split(":");
      return { value, label };
    });
  }
  //Senão assume que o valor está cadastrado no formato de lista (Ex:5,10,15), onde o valor é igual ao label
  else {
    return valorOpcoes.split(",").map((item) => {
      return { value: item.trim(), label: item.trim() };
    });
  }
}

/*
 * Retorna apenas os atributos que possuem valor em formato de query de url
 * Ex: obj = {
 *       nome: Joao,
 *       idade: 35
 *       email: ""
 *     }
 *     O retorno será nome=Joao&idade=35
 */
export function fn_getQueryUrl(obj) {
  return Object.entries(obj || {})
    .filter((f) => Boolean(f[1]))
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
}

/*
 * Transforma a query da url em objeto
 */
export function fn_queryToObject(query) {
  return Object.fromEntries(new URLSearchParams(query));
}

/*
 * Retorna a posição da lupa sobre a imagem e a parte ampliada da imagem que ela mostra (estilo da lupa).
 * Retorna null quando o mouse está fora da imagem.
 * @mouseX, mouseY: posição do mouse dentro da imagem (px)
 * @largura, altura: tamanho da imagem apresentada na tela (px)
 * @tamanhoLupa: diâmetro da lupa (px)
 * @zoom: aumento da imagem dentro da lupa
 */
export function fn_getPosicaoLupa({
  mouseX,
  mouseY,
  largura,
  altura,
  tamanhoLupa,
  zoom,
}) {
  if (mouseX < 0 || mouseY < 0 || mouseX > largura || mouseY > altura) {
    return null;
  }

  const metade = tamanhoLupa / 2;

  return {
    //A lupa fica centralizada no mouse
    left: mouseX - metade,
    top: mouseY - metade,
    //A imagem ampliada é deslocada para o ponto do mouse ficar no centro da lupa
    backgroundSize: `${largura * zoom}px ${altura * zoom}px`,
    backgroundPosition: `${metade - mouseX * zoom}px ${metade - mouseY * zoom}px`,
  };
}

/*
 * Retorna a rotação da imagem (0, 90, 180 ou 270 graus) depois de girar 90 graus.
 * @rotacaoAtual: rotação atual em graus
 * @sentido: 1 = para a direita (horário); -1 = para a esquerda (anti-horário)
 */
export function fn_getRotacaoImagem(rotacaoAtual, sentido) {
  return (((rotacaoAtual + 90 * sentido) % 360) + 360) % 360;
}

/*
 * Retorna o tamanho da imagem girada: de lado (90 e 270 graus), a largura e a altura trocam
 */
export function fn_getTamanhoImagemGirada(largura, altura, graus) {
  const isDeLado = graus == 90 || graus == 270;

  return isDeLado ? { largura: altura, altura: largura } : { largura, altura };
}

/*
 * Gera, no navegador, uma cópia girada da imagem e retorna o endereço dela (blob), para a visualização.
 * O arquivo do servidor não é alterado. Gera a partir da imagem original, para não perder qualidade a cada giro.
 * Quem chama libera o endereço (URL.revokeObjectURL) quando não precisar mais dele.
 * @urlImagem: endereço da imagem original
 * @graus: 90, 180 ou 270
 * @tipo: tipo da imagem gerada (image/jpeg para foto/escaneado, que fica menor; image/png para os demais)
 */
export function fn_girarImagem(urlImagem, graus, tipo = "image/png") {
  return new Promise((resolve, reject) => {
    const imagem = new Image();

    imagem.onload = () => {
      const largura = imagem.naturalWidth;
      const altura = imagem.naturalHeight;
      const tamanho = fn_getTamanhoImagemGirada(largura, altura, graus);

      const canvas = document.createElement("canvas");
      canvas.width = tamanho.largura;
      canvas.height = tamanho.altura;

      const contexto = canvas.getContext("2d");
      if (!contexto) {
        reject(new Error("O navegador não permitiu girar a imagem."));
        return;
      }

      //Gira em torno do centro e desenha a imagem centralizada
      contexto.translate(tamanho.largura / 2, tamanho.altura / 2);
      contexto.rotate((graus * Math.PI) / 180);
      contexto.drawImage(imagem, -largura / 2, -altura / 2);

      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(URL.createObjectURL(blob))
            : reject(new Error("Não foi possível gerar a imagem girada.")),
        tipo,
        0.92, //qualidade do jpeg
      );
    };

    imagem.onerror = () =>
      reject(new Error("Não foi possível carregar a imagem para girar."));

    imagem.src = urlImagem;
  });
}
